import { ArrowRight, ShieldCheck, Handshake, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";

const HERO_IMAGE =
    "https://images.pexels.com/photos/13025947/pexels-photo-13025947.jpeg";

export default function Hero() {
    return (
        <section className="relative pt-28 lg:pt-32 pb-16 lg:pb-24 overflow-hidden">
            {/* Subtle background grid */}
            <div className="absolute inset-0 -z-10 bg-grid opacity-[0.35] [mask-image:radial-gradient(ellipse_at_top,black,transparent_70%)]" />
            <div className="absolute inset-0 -z-10 bg-gradient-subtle" />

            <div className="container-px mx-auto max-w-7xl">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
                    {/* Copy */}
                    <div className="lg:col-span-6 animate-fade-up">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border bg-background/60 backdrop-blur">
                            <span className="w-1.5 h-1.5 rounded-full bg-success" />
                            <span className="text-xs font-medium tracking-wide text-muted-foreground">
                                B2B Wholesale · Supplier Sourcing · USA
                            </span>
                        </div>

                        <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight text-foreground text-balance">
                            Connecting brands and suppliers
                            <span className="block text-primary-variant">across global markets.</span>
                        </h1>

                        <p className="mt-5 lg:mt-6 max-w-xl text-base lg:text-lg text-muted-foreground leading-relaxed">
                            Zaroor Global Trading LLC is a US-registered wholesale, Amazon sourcing
                            and procurement company — founded in 2026 — helping businesses worldwide
                            find, verify and work with reliable suppliers.
                        </p>

                        <div className="mt-8 flex flex-wrap gap-3">
                            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary-variant h-11 px-6">
                                <a href="#contact">
                                    Start a Conversation <ArrowRight className="w-4 h-4" />
                                </a>
                            </Button>
                            <Button asChild size="lg" variant="outline" className="h-11 px-6 border-border hover:bg-muted text-foreground">
                                <a href="#about">Learn More</a>
                            </Button>
                        </div>

                        {/* Inline trust signals — values, not unverified metrics */}
                        <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg">
                            {[
                                { icon: Handshake, label: "Founder-led", sub: "B2B operations" },
                                { icon: ShieldCheck, label: "US-registered", sub: "Wyoming LLC" },
                                { icon: MapPin, label: "Casper, WY", sub: "Headquarters" }
                            ].map((s) => (
                                <div key={s.sub} className="flex flex-col">
                                    <s.icon className="w-4 h-4 text-primary-glow mb-2" />
                                    <span className="font-display text-base lg:text-lg font-semibold text-foreground leading-tight">{s.label}</span>
                                    <span className="text-xs text-muted-foreground mt-0.5">{s.sub}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Visual */}
                    <div className="lg:col-span-6 animate-fade-in">
                        <div className="relative">
                            <div className="absolute -inset-4 rounded-2xl bg-gradient-primary opacity-[0.06] blur-2xl" />
                            <div className="relative rounded-2xl overflow-hidden border border-border shadow-elegant">
                                <div className="aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5] w-full bg-muted">
                                    <img
                                        src={HERO_IMAGE}
                                        alt="Global wholesale shipping port"
                                        className="w-full h-full object-cover"
                                        loading="eager"
                                    />
                                </div>
                                {/* Overlay card — honest snapshot */}
                                <div className="absolute left-4 right-4 bottom-4 lg:left-6 lg:right-auto lg:bottom-6 lg:max-w-xs">
                                    <div className="rounded-xl bg-background/95 backdrop-blur border border-border p-4 shadow-soft">
                                        <div className="flex items-center gap-2">
                                            <span className="w-2 h-2 rounded-full bg-success" />
                                            <span className="text-xs font-medium text-muted-foreground tracking-wide uppercase">
                                                Now accepting partners
                                            </span>
                                        </div>
                                        <p className="mt-2 text-sm font-medium text-foreground leading-snug">
                                            Onboarding brands &amp; suppliers for our 2026 sourcing network.
                                        </p>
                                        <p className="mt-2 text-xs text-muted-foreground">
                                            Replies within 1 business day · Mon–Fri (MT)
                                        </p>
                                    </div>
                                </div>
                            </div>
                            {/* Floating credential card */}
                            <div className="hidden lg:flex absolute -right-6 top-10 w-56 rounded-xl bg-background border border-border shadow-soft p-4 flex-col gap-2">
                                <div className="flex items-center gap-2 text-primary">
                                    <ShieldCheck className="w-4 h-4" />
                                    <span className="text-xs font-semibold tracking-wide uppercase">Compliance</span>
                                </div>
                                <p className="text-sm text-foreground font-medium leading-snug">
                                    US-registered wholesale trader · Full documentation on every transaction.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
